import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CalendarCheck, Airplane, CalendarStar, ArrowsClockwise, Trash, Plus, CheckCircle, XCircle, Clock, DownloadSimple, UploadSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const SHIFT_TYPES = ["Callback", "Overtime", "Paid Duty", "Special Event", "Regular Shift", "Other"];
const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const STATUS_STYLE = { pending: "bg-amber-500/15 text-amber-400", approved: "bg-emerald-500/15 text-emerald-400", declined: "bg-rose-500/15 text-rose-400" };
const today = () => new Date().toISOString().slice(0, 10);

export default function SchedulePage() {
  return (
    <div className="animate-fade-up space-y-6" data-testid="schedule-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center"><CalendarCheck className="h-6 w-6 text-primary" weight="fill" /></div>
        <div>
          <p className="overline text-primary">Work schedule</p>
          <h1 className="text-3xl font-black tracking-tight">Schedule &amp; Leave</h1>
        </div>
      </div>

      <Tabs defaultValue="leave">
        <TabsList className="flex flex-wrap h-auto" data-testid="schedule-tabs">
          <TabsTrigger value="leave" data-testid="tab-leave"><Airplane className="h-4 w-4 mr-1.5" /> Leave</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability"><Clock className="h-4 w-4 mr-1.5" /> OT Availability</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays"><CalendarStar className="h-4 w-4 mr-1.5" /> Holidays</TabsTrigger>
          <TabsTrigger value="io" data-testid="tab-io"><ArrowsClockwise className="h-4 w-4 mr-1.5" /> Import / Export</TabsTrigger>
        </TabsList>
        <TabsContent value="leave" className="mt-5"><LeaveTab /></TabsContent>
        <TabsContent value="availability" className="mt-5"><AvailabilityTab /></TabsContent>
        <TabsContent value="holidays" className="mt-5"><HolidaysTab /></TabsContent>
        <TabsContent value="io" className="mt-5"><ImportExportTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Leave ---------------- */
function LeaveTab() {
  const qc = useQueryClient();
  const { data: meta } = useQuery({ queryKey: ["leave-meta"], queryFn: async () => (await api.get("/leave/meta")).data });
  const { data: leave = [] } = useQuery({ queryKey: ["leave"], queryFn: async () => (await api.get("/leave")).data });
  const [form, setForm] = useState({ leave_type: "vacation", start_date: today(), end_date: today(), note: "" });
  const [editingBal, setEditingBal] = useState(false);
  const [bal, setBal] = useState({});

  const create = useMutation({
    mutationFn: async () => (await api.post("/leave", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); qc.invalidateQueries({ queryKey: ["leave-meta"] }); toast.success("Leave request added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/leave/${id}/status`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); qc.invalidateQueries({ queryKey: ["leave-meta"] }); },
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/leave/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); qc.invalidateQueries({ queryKey: ["leave-meta"] }); },
  });
  const saveBal = useMutation({
    mutationFn: async () => (await api.put("/leave/balances", { balances: bal })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave-meta"] }); setEditingBal(false); toast.success("Entitlements saved"); },
  });

  const startEditBal = () => {
    const b = {};
    (meta?.balances || []).forEach((x) => { b[x.id] = x.entitlement ?? ""; });
    setBal(b); setEditingBal(true);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <Card className="p-5">
          <p className="font-bold mb-3">Request leave</p>
          <div className="space-y-3">
            <div>
              <Label>Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.leave_types || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}{t.paid ? "" : " (unpaid)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
              <div><Label>To</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" data-testid="leave-note" /></div>
            <Button className="w-full" onClick={() => create.mutate()} disabled={create.isPending} data-testid="leave-submit"><Plus className="h-4 w-4 mr-1.5" /> Add leave request</Button>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold">Balances ({meta?.year})</p>
            {editingBal
              ? <Button size="sm" onClick={() => saveBal.mutate()} data-testid="leave-save-balances">Save</Button>
              : <Button size="sm" variant="outline" onClick={startEditBal} data-testid="leave-edit-balances">Set entitlements</Button>}
          </div>
          <div className="space-y-2">
            {(meta?.balances || []).map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: b.color }} />{b.label}</span>
                {editingBal ? (
                  <Input type="number" className="w-20 h-8" value={bal[b.id] ?? ""} placeholder="—"
                    onChange={(e) => setBal({ ...bal, [b.id]: e.target.value })} data-testid={`bal-input-${b.id}`} />
                ) : (
                  <span className="text-muted-foreground" data-testid={`bal-${b.id}`}>
                    {b.entitlement != null ? <><span className="text-foreground font-semibold">{b.remaining}</span> / {b.entitlement} left</> : <>{b.used} used</>}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="lg:col-span-2 p-5">
        <p className="font-bold mb-3">Your leave</p>
        {leave.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No leave requests yet.</p> : (
          <div className="space-y-2" data-testid="leave-list">
            {leave.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`leave-row-${l.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-8 w-1.5 rounded-full shrink-0" style={{ background: l.color }} />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{l.leave_label} <span className="text-muted-foreground font-normal">· {l.days}d</span></p>
                    <p className="text-xs text-muted-foreground">{l.start_date}{l.end_date !== l.start_date ? ` → ${l.end_date}` : ""}{!l.paid && l.impact ? ` · est. −${money(l.impact)}` : l.paid ? " · paid" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Badge className={`${STATUS_STYLE[l.status]} border-0`} data-testid={`leave-status-${l.id}`}>{l.status}</Badge>
                  {l.status !== "approved" && <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400" title="Approve" onClick={() => setStatus.mutate({ id: l.id, status: "approved" })} data-testid={`leave-approve-${l.id}`}><CheckCircle className="h-4 w-4" /></Button>}
                  {l.status !== "declined" && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" title="Decline" onClick={() => setStatus.mutate({ id: l.id, status: "declined" })} data-testid={`leave-decline-${l.id}`}><XCircle className="h-4 w-4" /></Button>}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" title="Delete" onClick={() => del.mutate(l.id)} data-testid={`leave-delete-${l.id}`}><Trash className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Availability ---------------- */
function AvailabilityTab() {
  const qc = useQueryClient();
  const { data: avails = [] } = useQuery({ queryKey: ["availability"], queryFn: async () => (await api.get("/availability")).data });
  const { data: matches = [] } = useQuery({ queryKey: ["avail-matches"], queryFn: async () => (await api.get("/availability/matching-shifts")).data });
  const [f, setF] = useState({ start_date: today(), end_date: today(), preferred_start: "08:00", preferred_end: "18:00", shift_types: [], weekdays: [], note: "" });

  const create = useMutation({
    mutationFn: async () => (await api.post("/availability", { ...f, available: true })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["availability"] }); qc.invalidateQueries({ queryKey: ["avail-matches"] }); toast.success("Availability saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/availability/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["availability"] }); qc.invalidateQueries({ queryKey: ["avail-matches"] }); } });
  const accept = useMutation({ mutationFn: async (id) => api.post(`/shifts/${id}/request`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["avail-matches"] }); toast.success("Request sent — track it in the Marketplace"); }, onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)) });
  const dismiss = useMutation({ mutationFn: async (id) => api.post(`/availability/dismiss/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["avail-matches"] }) });

  const toggle = (key, val) => setF((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val] }));

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1 p-5 space-y-3">
        <p className="font-bold">I'm available for extra work</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>From</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="avail-start" /></div>
          <div><Label>To</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="avail-end" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Preferred from</Label><Input type="time" value={f.preferred_start} onChange={(e) => setF({ ...f, preferred_start: e.target.value })} data-testid="avail-pref-start" /></div>
          <div><Label>Preferred to</Label><Input type="time" value={f.preferred_end} onChange={(e) => setF({ ...f, preferred_end: e.target.value })} data-testid="avail-pref-end" /></div>
        </div>
        <div>
          <Label>Shift types</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {SHIFT_TYPES.map((s) => (
              <button key={s} type="button" onClick={() => toggle("shift_types", s)} data-testid={`avail-type-${s.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.shift_types.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{s}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Repeat on (optional — recurring)</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {WEEKDAYS.map(([lbl, n]) => (
              <button key={n} type="button" onClick={() => toggle("weekdays", n)} data-testid={`avail-wd-${n}`}
                className={`text-xs w-9 py-1 rounded-md border transition-colors ${f.weekdays.includes(n) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <Button className="w-full" onClick={() => create.mutate()} disabled={create.isPending} data-testid="avail-submit"><Plus className="h-4 w-4 mr-1.5" /> Save availability</Button>

        <div className="pt-2 space-y-2">
          {avails.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm rounded-md border border-border p-2.5" data-testid={`avail-row-${a.id}`}>
              <div>
                <p className="font-medium">{a.start_date}{a.end_date !== a.start_date ? ` → ${a.end_date}` : ""}</p>
                <p className="text-xs text-muted-foreground">{a.shift_types.length ? a.shift_types.join(", ") : "Any type"}{a.weekdays.length ? ` · ${a.weekdays.map((n) => WEEKDAYS[n][0]).join("/")}` : ""}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => del.mutate(a.id)} data-testid={`avail-delete-${a.id}`}><Trash className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="lg:col-span-2 p-5">
        <p className="font-bold mb-1">Shifts matching your availability</p>
        <p className="text-xs text-muted-foreground mb-3">Posted shifts that fit the dates and shift types you're available for.</p>
        {matches.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center" data-testid="no-matches">No matching shifts right now. We'll notify you when one is posted.</p> : (
          <div className="space-y-2" data-testid="matches-list">
            {matches.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`match-row-${m.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{m.shift_type} <span className="text-muted-foreground font-normal">· {m.date}</span></p>
                  <p className="text-xs text-muted-foreground truncate">{[m.organization_name, m.unit, m.start_time && `${m.start_time}–${m.end_time}`].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => accept.mutate(m.id)} data-testid={`match-accept-${m.id}`}>Accept</Button>
                  <Button size="sm" variant="outline" onClick={() => dismiss.mutate(m.id)} data-testid={`match-decline-${m.id}`}>Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Holidays ---------------- */
function HolidaysTab() {
  const year = new Date().getFullYear();
  const [yr, setYr] = useState(year);
  const [region, setRegion] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["holidays", yr, region], queryFn: async () => (await api.get(`/holidays?year=${yr}${region ? `&region=${region}` : ""}`)).data });

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="font-bold">Statutory &amp; public holidays</p>
          <p className="text-xs text-muted-foreground">{data?.country}{data?.supported === false ? " — holiday data not available for your country yet" : ""}</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label>Region code</Label><Input className="w-32 h-9" placeholder="e.g. CA-ON" value={region} onChange={(e) => setRegion(e.target.value.toUpperCase())} data-testid="holiday-region" /></div>
          <div><Label>Year</Label>
            <Select value={String(yr)} onValueChange={(v) => setYr(Number(v))}>
              <SelectTrigger className="w-24 h-9" data-testid="holiday-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1, year + 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {isLoading ? <p className="text-muted-foreground text-sm py-8 text-center">Loading holidays…</p>
        : (data?.holidays || []).length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center" data-testid="no-holidays">No holiday data available.</p>
          : (
            <div className="space-y-2" data-testid="holiday-list">
              {data.holidays.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`holiday-row-${h.date}`}>
                  <div className="flex items-center gap-3">
                    <div className="text-center w-12 shrink-0">
                      <p className="text-xs text-muted-foreground uppercase">{new Date(h.date).toLocaleString("en", { month: "short" })}</p>
                      <p className="text-lg font-black leading-none">{h.date.slice(8)}</p>
                    </div>
                    <div>
                      <p className="font-medium">{h.name}</p>
                      <p className="text-xs text-muted-foreground">{h.national ? "National" : "Regional"} · Holiday premium est. {money(h.premium_estimate)}</p>
                    </div>
                  </div>
                  {h.scheduled && <Badge className="bg-primary/15 text-primary border-0" data-testid={`holiday-scheduled-${h.date}`}>You're scheduled</Badge>}
                </div>
              ))}
            </div>
          )}
    </Card>
  );
}

/* ---------------- Import / Export ---------------- */
function ImportExportTab() {
  const [range, setRange] = useState({ start: today().slice(0, 8) + "01", end: today() });
  const [imp, setImp] = useState({ format: "ics", content: "", hours: 8 });
  const backend = process.env.REACT_APP_BACKEND_URL;

  const doExport = (fmt) => {
    const url = `${backend}/api/schedule/export?start=${range.start}&end=${range.end}&fmt=${fmt}`;
    window.open(url, "_blank");
  };
  const runImport = useMutation({
    mutationFn: async () => (await api.post("/schedule/import", imp)).data,
    onSuccess: (d) => toast.success(`Imported ${d.imported} shift(s)`, { description: d.skipped ? `${d.skipped} skipped (already existed or invalid)` : undefined }),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImp((p) => ({ ...p, content: String(reader.result), format: file.name.toLowerCase().endsWith(".csv") ? "csv" : "ics" }));
    reader.readAsText(file);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="p-5 space-y-3">
        <p className="font-bold flex items-center gap-2"><DownloadSimple className="h-5 w-5 text-primary" /> Export schedule</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="export-start" /></div>
          <div><Label>To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="export-end" /></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => doExport("ics")} data-testid="export-ics">Calendar (.ics)</Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="export-pdf">PDF</Button>
          <Button variant="outline" onClick={() => doExport("csv")} data-testid="export-csv">CSV</Button>
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="export-xlsx">Excel</Button>
        </div>
        <p className="text-xs text-muted-foreground">The .ics file imports into Google, Apple or Outlook calendars. Two-way sync is coming in Phase 2.</p>
      </Card>

      <Card className="p-5 space-y-3">
        <p className="font-bold flex items-center gap-2"><UploadSimple className="h-5 w-5 text-primary" /> Import schedule</p>
        <div>
          <Label>Upload .ics or .csv</Label>
          <Input type="file" accept=".ics,.csv" onChange={onFile} data-testid="import-file" />
          <p className="text-xs text-muted-foreground mt-1">CSV format: first column a date (YYYY-MM-DD), optional label in the second column.</p>
        </div>
        <Textarea rows={4} placeholder="…or paste ICS / CSV content here" value={imp.content} onChange={(e) => setImp({ ...imp, content: e.target.value })} data-testid="import-content" />
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label>Hours per imported shift</Label><Input type="number" value={imp.hours} onChange={(e) => setImp({ ...imp, hours: e.target.value })} data-testid="import-hours" /></div>
          <Button onClick={() => runImport.mutate()} disabled={runImport.isPending || !imp.content} data-testid="import-submit">Import</Button>
        </div>
        <p className="text-xs text-amber-400/90 flex items-start gap-1.5"><Warning className="h-4 w-4 shrink-0 mt-0.5" /> Imported dates are added as Regular Hours entries. Existing dates are skipped.</p>
      </Card>
    </div>
  );
}
